import { useState, useEffect } from 'react';
import { Shield, CheckCircle, Clock, AlertTriangle, Upload } from 'lucide-react';
import { PageShell, Modal, FG, INP, SEL, Row2, BtnPrimary, BtnGhost, Spinner, fmtINR, fmtDate, useBanner, useWalletUser } from './WalletShared';
import walletController from '../../controllers/walletController';

const KYC_STATUS_CFG = {
  not_submitted: { color:'#64748b', bg:'#f1f5f9', label:'Not Submitted', Icon:AlertTriangle },
  pending:       { color:'#92400e', bg:'#fef3c7', label:'Under Review',  Icon:Clock },
  approved:      { color:'#15803d', bg:'#dcfce7', label:'Verified',      Icon:CheckCircle },
  rejected:      { color:'#b91c1c', bg:'#fef2f2', label:'Rejected',      Icon:AlertTriangle },
};

const LimitsKyc = () => {
  const { userId } = useWalletUser();
  const { banner, notify, clearBanner } = useBanner();
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [showKyc,  setShowKyc]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [form,     setForm]     = useState({ docType:'aadhaar', docNumber:'', name:'', dob:'' });
  const f = (k,v) => setForm(p => ({ ...p, [k]:v }));

  const load = async () => {
    setLoading(true);
    const res = await walletController.getLimitsKyc(userId);
    if (res.success) setData(res.data);
    else notify('error', res.message);
    setLoading(false);
  };
  useEffect(() => { load(); }, [userId]);

  const handleKyc = async () => {
    if (!form.docNumber||!form.name) return notify('error','Document number and name are required');
    setSaving(true);
    const res = await walletController.submitKyc(userId, form);
    setSaving(false);
    if (res.success) { notify('success', res.message); setShowKyc(false); load(); }
    else notify('error', res.message);
  };

  if (loading) return <div style={{ display:'flex', justifyContent:'center', alignItems:'center', minHeight:'60vh', gap:10, color:'#64748b' }}><Spinner size={28}/></div>;

  const kycCfg = KYC_STATUS_CFG[data?.kyc?.status||'not_submitted'];
  const KycIcon = kycCfg.Icon;

  return (
    <PageShell title="Limits & KYC" subtitle="Transaction limits and KYC verification" icon={Shield}
      banner={banner} onCloseBanner={clearBanner}>

      {/* KYC Status */}
      <div style={{ background:'#fff', borderRadius:12, border:'1px solid #f1f5f9', padding:'18px 20px', marginBottom:20 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <span style={{ fontSize:15, fontWeight:700, color:'#1e293b' }}>KYC Verification</span>
          <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'4px 10px', borderRadius:8, fontSize:12, fontWeight:600, background:kycCfg.bg, color:kycCfg.color }}>
            <KycIcon size={12}/>{kycCfg.label}
          </span>
        </div>
        {data?.kyc?.status==='approved'
          ? <div style={{ fontSize:13, color:'#475569' }}>KYC verified on {fmtDate(data.kyc.verifiedAt)} · Doc: {data.kyc.docType?.toUpperCase()} {data.kyc.docNumber}</div>
          : data?.kyc?.status==='pending'
            ? <div style={{ fontSize:13, color:'#92400e' }}>Under review since {fmtDate(data.kyc.submittedAt)}. Usually takes 1–2 business days.</div>
            : <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <p style={{ fontSize:13, color:'#64748b', margin:0 }}>Complete KYC to increase transaction limits and access all features.</p>
                <button onClick={() => setShowKyc(true)} style={{ display:'flex', alignItems:'center', gap:5, padding:'8px 14px', background:'#1e293b', color:'#fff', border:'none', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer' }}>
                  <Upload size={13}/>Submit KYC
                </button>
              </div>}
      </div>

      {/* Limits */}
      <div style={{ background:'#fff', borderRadius:12, border:'1px solid #f1f5f9', padding:'18px 20px' }}>
        <div style={{ fontSize:15, fontWeight:700, color:'#1e293b', marginBottom:16 }}>Transaction Limits</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:14 }}>
          {[
            { label:'Daily Limit',       used:data?.limits?.dailyUsed||0,   max:data?.limits?.dailyMax||50000 },
            { label:'Monthly Limit',     used:data?.limits?.monthlyUsed||0, max:data?.limits?.monthlyMax||200000 },
            { label:'Single Transaction',used:0,                            max:data?.limits?.singleMax||25000 },
            { label:'Wallet Balance',    used:data?.balance||0,             max:data?.limits?.balanceMax||100000 },
          ].map((l,i) => {
            const pct = Math.min(100, (l.used/l.max)*100);
            return (
              <div key={i} style={{ padding:'14px 16px', background:'#f8fafc', borderRadius:10 }}>
                <div style={{ fontSize:11, color:'#64748b', marginBottom:6, fontWeight:500 }}>{l.label}</div>
                <div style={{ fontSize:14, fontWeight:700, color:'#1e293b', marginBottom:4 }}>{fmtINR(l.max)}</div>
                <div style={{ height:4, background:'#e2e8f0', borderRadius:2, marginBottom:4, overflow:'hidden' }}>
                  <div style={{ height:'100%', width:`${pct}%`, background:pct>80?'#ef4444':'#3b82f6', borderRadius:2 }}/>
                </div>
                {l.used>0 && <div style={{ fontSize:10, color:'#94a3b8' }}>Used: {fmtINR(l.used)} ({pct.toFixed(0)}%)</div>}
              </div>
            );
          })}
        </div>
      </div>

      {showKyc && (
        <Modal title="Submit KYC Documents" onClose={() => setShowKyc(false)}
          footer={<><BtnGhost onClick={() => setShowKyc(false)}>Cancel</BtnGhost><BtnPrimary onClick={handleKyc} disabled={saving}>{saving?<><Spinner size={13}/>Submitting…</>:<><Upload size={13}/>Submit KYC</>}</BtnPrimary></>}>
          <Row2>
            <FG label="Document Type">
              <SEL value={form.docType} onChange={e => f('docType',e.target.value)}>
                <option value="aadhaar">Aadhaar Card</option>
                <option value="pan">PAN Card</option>
                <option value="passport">Passport</option>
                <option value="driving_license">Driving License</option>
                <option value="voter_id">Voter ID</option>
              </SEL>
            </FG>
            <FG label="Document Number *"><INP value={form.docNumber} onChange={e => f('docNumber',e.target.value.toUpperCase())} placeholder="Document ID number"/></FG>
          </Row2>
          <Row2>
            <FG label="Full Name *"><INP value={form.name} onChange={e => f('name',e.target.value)} placeholder="As on document"/></FG>
            <FG label="Date of Birth"><INP type="date" value={form.dob} onChange={e => f('dob',e.target.value)}/></FG>
          </Row2>
          <div style={{ background:'#f8fafc', borderRadius:8, padding:'10px 12px', fontSize:12, color:'#64748b' }}>
            Your KYC documents will be securely stored and verified within 1–2 business days.
          </div>
        </Modal>
      )}
    </PageShell>
  );
};
export default LimitsKyc;